import { useState } from 'react';
import {
  Modal,
  Button,
  Form,
  Spinner,
  Col,
  Row,
  ToastContainer,
  Toast,
} from 'react-bootstrap';
import { supabase } from '../lib/supabase';
import type { DropdownSelectOption, UserOrganisation } from '../types/types';
import { useSelector } from 'react-redux';
import DropdownSelect from './common/DropdownSelect';
import { countryList } from '../const/const';
import { IndustrySelect } from './common/IndustrySelect';
import { tracking } from '../util/tracking';
import { gToastPosition, gToastSentiment } from '../util/constst';

// Use a new name to avoid conflict with the original component
interface CreateSupplierModalSingleStepProps {
  show: boolean;
  onHide: () => void;
  onCreated?: () => void;
  onFinished: () => void;
}

export default function CreateSupplierModalSingleStep({
  show,
  onHide,
  onCreated,
  onFinished,
}: CreateSupplierModalSingleStepProps) {
  // 1. STATE MANAGEMENT
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const selectedOrganisation: UserOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected
  );

  // --- Supplier Info State (The only remaining state) ---
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [website, setWebsite] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');

  // 🔑 NEW/MOVED FIELD: Mandatory email
  const [supplierEmail, setSupplierEmail] = useState('');

  // --- New State for Email Status ---
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailUniqueError, setEmailUniqueError] = useState<string | null>(null);

  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);

  // 2. HELPER FUNCTIONS

  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const resetState = () => {
    setErrors({});
    setEmailUniqueError(null);
    setIsCheckingEmail(false);
    setLoading(false);
    // Reset all fields
    setName('');
    setIndustry('');
    setWebsite('');
    setRegistrationNumber('');
    setStreet('');
    setCity('');
    setState('');
    setPostalCode('');
    setCountry('');
    setSupplierEmail(''); // Reset new email field
    // Note: step is not reset, as it's the only step now
  };

  const handleClose = () => {
    resetState();
    onHide();
  };

  // --- New Asynchronous OnBlur Handler ---
  const handleEmailBlur = async () => {
    // 1. Basic Local Validation Check
    const emailValue = supplierEmail.trim();
    if (!emailValue || !emailValue.includes('@')) {
      // Clear any previous async error if basic validation fails
      setEmailUniqueError('Valid Email is required (for invitations)');
      return;
    }

    // 2. Clear previous errors and start loading
    setEmailUniqueError(null);
    setIsCheckingEmail(true);

    // 3. Database Check
    const uniquenessError = await validateSupplierExists(emailValue);

    setIsCheckingEmail(false);

    if (uniquenessError) {
      setEmailUniqueError(uniquenessError);
      // Show a toast for immediate visibility
      showToastMessage(uniquenessError);
    } else {
      setEmailUniqueError(null);
    }
  };

  // Re-using the core check logic, now accepting the email string
  const validateSupplierExists = async (
    emailToCheck: string
  ): Promise<string | null> => {
    // We already checked for basic email validity in handleEmailBlur
    if (!selectedOrganisation?.organisation?.id) return null;

    // This query checks if any supplier with this email is ALREADY linked to this buyer org.
    const { data, error } = await supabase
      .from('suppliers')
      .select(
        `
            id,
            company_name,
            buyer_supplier_link!inner(supplier_id)
        `
      )
      .eq('email', emailToCheck)
      .eq(
        'buyer_supplier_link.buyer_org_id',
        selectedOrganisation.organisation.id
      )
      .limit(1);

    if (error) {
      console.error('Error checking existing supplier email:', error);
      return 'A database error occurred while checking supplier uniqueness.';
    }

    if (data && data.length > 0) {
      const existingSupplier = data[0];
      return `A supplier with this email already exists for your organisation: ${existingSupplier.company_name}`;
    }

    return null; // Email is unique for this organization
  };

  // 3. LOGIC (FORMER STEP 2, NOW THE ONLY STEP)

  const validateDetails = () => {
    const newErrors: { [key: string]: string } = {};
    if (!name.trim()) newErrors.name = 'Supplier Name is required';
    if (!selectedOrganisation?.organisation?.id)
      newErrors.organisation = 'Organisation is required';
    if (!industry) newErrors.industry = 'Industry is required';
    if (!registrationNumber.trim())
      newErrors.registrationNumber = 'Registration Number is required';
    if (!country.trim()) newErrors.country = 'Country is required';
    if (!street.trim()) newErrors.street = 'Street is required';
    if (!city.trim()) newErrors.city = 'City is required';
    if (!postalCode.trim()) newErrors.postalCode = 'Postal Code is required';

    // 🔑 NEW VALIDATION: Email is mandatory
    if (!supplierEmail.trim() || !supplierEmail.includes('@'))
      newErrors.supplierEmail = 'Valid Email is required (for invitations)';

    return newErrors;
  };

  const handleStepDetails = async () => {
    const newErrors = validateDetails();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // CRITICAL BLOCK: Don't proceed if the email check is still running
    if (isCheckingEmail) {
      showToastMessage('Please wait for the email check to complete.');
      return;
    }

    if (emailUniqueError) {
      showToastMessage(emailUniqueError);
      return;
    }

    tracking.log('UI', 'supplier_create', {
      orgId: selectedOrganisation?.organisation?.id,
    });

    setErrors({});
    if (!selectedOrganisation?.organisation?.id) return;
    setLoading(true);

    // 🔑 We only have one path now: creating a new supplier (no search/selection)
    // Call the RPC to create the supplier and its address
    const { data: createdSupplierId, error } = await supabase.rpc(
      'create_supplier',
      {
        org_id: selectedOrganisation?.organisation.id,
        supplier_name: name,
        industry_id: industry,
        website,
        registration_number: registrationNumber,
        p_street: street,
        p_city: city,
        p_state: state,
        p_postal_code: postalCode,
        p_country: country,
        // 🔑 PASS THE MANDATORY EMAIL FIELD TO THE RPC
        p_email: supplierEmail,
      }
    );

    if (error) {
      console.error('Error creating supplier with address:', error);
      setLoading(false);
      showToastMessage('Error creating supplier: ' + error.message);
      return;
    }

    // Now, ensure the link exists (this is done even if the RPC already handles it, for safety)
    const { error: linkError } = await supabase.rpc(
      'ensure_buyer_supplier_link',
      {
        p_buyer_org_id: selectedOrganisation?.organisation.id,
        p_supplier_id: createdSupplierId,
      }
    );

    setLoading(false);

    if (linkError) {
      console.error('Error ensuring buyer-supplier link:', linkError);
      showToastMessage(
        'Supplier created, but error linking to organisation: ' +
          linkError.message
      );
      onFinished?.();
      handleClose();
      return;
    }

    // 🔑 FINAL STEP: Supplier created and linked. Finish the process.
    onCreated?.(); // Optional callback for successful creation
    onFinished?.();
    showToastMessage('Supplier created and linked successfully!');
    handleClose();
  };

  // 4. OPTIONS MAPPING (Simplified)
  const countryOptions: DropdownSelectOption[] = countryList.map((c) => ({
    id: c.isoCode,
    label: c.countryName,
  }));

  const handleCountrySelected = (
    option: DropdownSelectOption | DropdownSelectOption[]
  ) => {
    setCountry((option as DropdownSelectOption).id);
  };

  const handleIndustryChanged = (
    value: string | number | boolean | string[] | undefined
  ) => {
    setIndustry((value as string) || '');
  };

  // 5. RENDER LOGIC (Simplified to Step 1/Details)

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      backdrop="static"
      keyboard={false}
      className="create-supplier-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>New Supplier Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <p className="text-muted small">
            Enter all required supplier details. This information will be owned
            by your organization.
          </p>

          <Row>
            <Col sm={12} md={6}>
              <Form.Group className="mb-3 position-relative">
                <Form.Label>
                  Supplier Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoFocus
                  placeholder="Supplier Corporation"
                  isInvalid={!!errors.name}
                />
                <Form.Control.Feedback tooltip type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col sm={12} md={6}>
              <Form.Group className="mb-3 position-relative">
                <Form.Label>
                  Supplier Email <span className="text-danger">*</span>
                </Form.Label>
                <div className="d-flex align-items-center">
                  <Form.Control
                    type="email"
                    value={supplierEmail}
                    onChange={(e) => {
                      setSupplierEmail(e.target.value);
                      // Clear async error as user starts typing again
                      setEmailUniqueError(null);
                    }}
                    onBlur={handleEmailBlur} // 🔑 NEW HANDLER
                    required
                    placeholder="invitations@supplier.com"
                    // Check if there are general errors OR a specific async error
                    isInvalid={!!errors.supplierEmail || !!emailUniqueError}
                  />
                  {isCheckingEmail && (
                    <Spinner
                      animation="border"
                      size="sm"
                      className="ms-2"
                      style={{ position: 'absolute', right: '10px' }}
                    />
                  )}
                </div>
                <Form.Control.Feedback tooltip type="invalid">
                  {errors.supplierEmail || emailUniqueError}
                </Form.Control.Feedback>
                <Form.Text muted>
                  This email is mandatory for sending assessments/invitations.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col sm={12} md={6}>
              <Form.Group className="mb-3 position-relative">
                <Form.Label>
                  Country of registration <span className="text-danger">*</span>
                </Form.Label>
                <DropdownSelect
                  options={countryOptions}
                  full
                  selectedIds={country}
                  onValueChanged={handleCountrySelected}
                  isInvalid={!!errors.country}
                />
                <Form.Control.Feedback tooltip type="invalid">
                  {errors.country}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col sm={12} md={6}>
              <Form.Group className="mb-3 position-relative">
                <Form.Label>
                  Registration Number <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={registrationNumber}
                  onChange={(e) => setRegistrationNumber(e.target.value)}
                  placeholder="123456789"
                  isInvalid={!!errors.registrationNumber}
                />
                <Form.Control.Feedback tooltip type="invalid">
                  {errors.registrationNumber}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col sm={12} md={6}>
              <Form.Group className="mb-3 position-relative">
                <Form.Label>
                  Industry (NACE) <span className="text-danger">*</span>
                </Form.Label>
                <IndustrySelect
                  onChange={handleIndustryChanged}
                  value={industry}
                  isInvalid={!!errors.industry}
                />
                <Form.Control.Feedback tooltip type="invalid">
                  {errors.industry}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col sm={12} md={6}>
              <Form.Group className="mb-3 position-relative">
                <Form.Label>Website</Form.Label>
                <Form.Control
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://supplier.com"
                />
              </Form.Group>
            </Col>
          </Row>

          <hr />
          <h5>
            Primary Address <span className="text-danger">*</span>
          </h5>
          <Row>
            <Col sm={12} md={6}>
              <Form.Group className="mb-3 position-relative">
                <Form.Label>Street</Form.Label>
                <Form.Control
                  type="text"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  placeholder="123 Main St"
                  isInvalid={!!errors.street}
                />
                <Form.Control.Feedback tooltip type="invalid">
                  {errors.street}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col sm={12} md={6}>
              <Form.Group className="mb-3 position-relative">
                <Form.Label>City</Form.Label>
                <Form.Control
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Berlin"
                  isInvalid={!!errors.city}
                />
                <Form.Control.Feedback tooltip type="invalid">
                  {errors.city}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col sm={12} md={6}>
              <Form.Group className="mb-3 position-relative">
                <Form.Label>State</Form.Label>
                <Form.Control
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder="Berlin"
                />
              </Form.Group>
            </Col>
            <Col sm={12} md={6}>
              <Form.Group className="mb-3 position-relative">
                <Form.Label>Postal Code</Form.Label>
                <Form.Control
                  type="text"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  placeholder="10115"
                  isInvalid={!!errors.postalCode}
                />
                <Form.Control.Feedback tooltip type="invalid">
                  {errors.postalCode}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>

        <Button
          variant="success"
          onClick={handleStepDetails}
          disabled={loading}
        >
          {loading ? (
            <Spinner animation="border" size="sm" className="me-2" />
          ) : (
            'Create Supplier'
          )}
        </Button>
      </Modal.Footer>
      <ToastContainer position={gToastPosition} className="p-3">
        <Toast
          bg={gToastSentiment}
          onClose={() => setShowToast(false)}
          show={showToast}
          delay={3000}
          autohide
        >
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </Modal>
  );
}
